import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { getSkuCalculatedPrice } from "@/graphql/schemas/shared/utils/getPrices"
import { getStoreKeysWithChannels } from "@/graphql/schemas/shared/utils/getStoreKeysWithChannels"
import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getArticleConfiguration } from "../../shared/utils/getArticleInformation"
import { addCustomLineItemAction, removeCustomLineItemAction } from "../utils/cartHelpers"
import { getCart } from "../utils/getCart"

export const mutationUpdateLineItemsByQuantity = {
  async updateLineItemsByQuantity(parent, args, context, info) {
    let actions = []
    const { cartKey, cartId, items = [], lang, cdn } = args

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.CARTS,
      operation: Operations.UPDATE,
    })

    if (!items || items.length <= 0) return null

    const customerCart = await getCart(cartId, cartKey)

    if (!customerCart || customerCart?.customLineItems.length <= 0) return null

    const commerceApi = CommerceService.api()

    const { customLineItems, customerId, totalPrice } = customerCart
    const { currencyCode } = totalPrice
    const customer = await getCustomer({ id: customerId })
    const { stores: customerStores, customerGroup } = customer

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    const storeKeysWithChannels = await getStoreKeysWithChannels(customerStores)

    await Promise.all(
      items.map(async (item) => {
        // nothing should be done, because either slug or quantity is missing
        if (!item?.slug || item?.slug?.length <= 0 || !item?.quantity || item?.quantity <= 0) {
          return null
        }

        const targetCustomLineItem = customLineItems?.find((customLineItem) => item?.slug === customLineItem?.slug)

        // no item was found
        if (!targetCustomLineItem) return null

        const targetCustomLineItemCustomFields = targetCustomLineItem?.custom?.fields

        const articleConfig = await getArticleConfiguration({
          sku: targetCustomLineItemCustomFields.sku,
          productKey: targetCustomLineItemCustomFields.productId,
        })
        if (!articleConfig) {
          actions.push(removeCustomLineItemAction(targetCustomLineItem?.id))
        }

        let itemQuantity = Number(item.quantity)
        if (articleConfig?.sellableInDecimals) {
          // javascript things... Similar as in getPrices
          itemQuantity = Math.round(itemQuantity * 1000)
        }

        let updatedQuantity = itemQuantity - Number(targetCustomLineItem?.quantity)

        // nothing to do, same quantity was passed as in custom line item
        if (updatedQuantity === 0) return null

        const lineItemCustomPrice =
          typeof targetCustomLineItemCustomFields?.customPrice?.centAmount === "number"
            ? targetCustomLineItemCustomFields?.skuPrice * 100 ||
              targetCustomLineItemCustomFields.customPrice.centAmount
            : null

        // recalculate the price in case configuration in commercetools changed
        const price = await getSkuCalculatedPrice({
          sku: targetCustomLineItemCustomFields?.sku,
          channels: storeKeysWithChannels,
          group: customerGroup,
          country: customerCart?.billingAddress?.country,
          discountPercent: targetCustomLineItemCustomFields?.discountPercent,
          customPrice: lineItemCustomPrice,
          currencyCode,
          articleConfig,
          quantity: itemQuantity,
        })

        // no price was found
        if (!price) return null

        // need to first remove and then insert with the updated price
        if (targetCustomLineItemCustomFields?.priceId !== price?.key) {
          actions.push(removeCustomLineItemAction(targetCustomLineItem?.id))

          actions.push(
            addCustomLineItemAction(targetCustomLineItem, currencyCode, price, customerCart.id, itemQuantity),
          )

          return null
        }

        if (targetCustomLineItemCustomFields?.skuPrice !== price?.skuPrice) {
          actions.push(removeCustomLineItemAction(targetCustomLineItem?.id))

          actions.push(
            addCustomLineItemAction(targetCustomLineItem, currencyCode, price, customerCart.id, itemQuantity),
          )

          return null
        }

        if (updatedQuantity > 0) {
          actions.push(
            addCustomLineItemAction(
              targetCustomLineItem,
              currencyCode,
              { key: price?.key, skuPrice: price?.skuPrice, value: targetCustomLineItem.money },
              customerCart.id,
              updatedQuantity,
            ),
          )

          return null
        } else {
          // first remove the item in general and then add it back again
          // commercetools does not support removal by quantity for custom line items
          // so we first need to remove it and then add it back again with updates
          actions.push(removeCustomLineItemAction(targetCustomLineItem?.id))
          actions.push(
            addCustomLineItemAction(
              targetCustomLineItem,
              currencyCode,
              { key: price?.key, skuPrice: price?.skuPrice, value: targetCustomLineItem.money },
              customerCart.id,
              itemQuantity,
            ),
          )

          return null
        }
      }),
    )

    // remove all null items
    actions = actions.filter((n) => n)

    // no need for any action, since nothing was matched
    if (actions?.length <= 0) {
      return customerCart
    }

    // Retry logic for version conflicts
    const maxRetries = 3
    let retryCount = 0
    let currentCart = customerCart

    while (retryCount < maxRetries) {
      try {
        const payload = {
          version: currentCart.version,
          actions,
          storeKey: currentCart?.store?.key,
        }

        const updatedCart = (
          await commerceApi
            .carts()
            .withId({ ID: currentCart.id })
            .post({
              body: JSON.stringify(payload),
            })
            .execute()
        )?.body

        return updatedCart
      } catch (error) {
        // Check if it's a version conflict error
        const isVersionConflict =
          error.message?.includes("Version mismatch") ||
          error.message?.includes("Concurrent modification") ||
          error.message?.includes("different version") ||
          error.message?.includes("ConcurrentModification")

        if (isVersionConflict && retryCount < maxRetries - 1) {
          retryCount++
          // Wait a bit before retrying with exponential backoff
          const delay = Math.min(200 * Math.pow(2, retryCount - 1), 1000)
          await new Promise((resolve) => setTimeout(resolve, delay))

          // Refresh the cart to get the latest version and rebuild actions
          currentCart = await getCart(cartId, cartKey)
          if (!currentCart) {
            throw new Error("Cart not found after retry")
          }

          // Rebuild actions with the refreshed cart
          actions = []
          await Promise.all(
            items.map(async (item) => {
              const { lineItemId, quantity: itemQuantity } = item
              const targetCustomLineItem = currentCart.customLineItems.find(
                (customLineItem) => customLineItem.id === lineItemId,
              )

              if (!targetCustomLineItem) {
                return null
              }

              const updatedQuantity = itemQuantity
              const price = await getSkuCalculatedPrice({
                sku: targetCustomLineItem.slug,
                customerStores,
                customerGroup,
                currencyCode,
                client,
                lang,
              })

              if (updatedQuantity <= 0) {
                actions.push(removeCustomLineItemAction(targetCustomLineItem?.id))
                return null
              } else {
                actions.push(
                  addCustomLineItemAction(
                    targetCustomLineItem,
                    currencyCode,
                    {
                      key: price?.key,
                      skuPrice: price?.skuPrice,
                      value: targetCustomLineItem.money,
                    },
                    currentCart.id,
                    updatedQuantity,
                  ),
                )
                return null
              }
            }),
          )

          // Remove all null items
          actions = actions.filter((n) => n)

          // If no actions after refresh, return the current cart
          if (actions?.length <= 0) {
            return currentCart
          }

          continue
        }

        throw error
      }
    }

    throw new Error("Max retries exceeded for version conflicts")
  },
}
